import { useGuestSessionState } from '@shared/hooks/useGuestSessionState';

export type UseGuestSessionIdentityArgs = Readonly<{
  eventHandle: string | undefined | null;
  /** Set when the caller already has a name/email and the session can no longer win. */
  skip?: boolean;
}>;

export type UseGuestSessionIdentityReturn = Readonly<{
  /** The identified guest's display name, or `''` when we do not know them. */
  fullName: string;
  /** The identified guest's email, or `''` when we do not know them. */
  email: string;
  /** True once the read has settled (or was skipped). */
  isResolved: boolean;
}>;

/**
 * The viewing guest's own name and email, when the event session knows who they are —
 * a guest who followed their invite link, RSVP'd, or logged in.
 *
 * Shares one session read with `useRsvpAttendanceStatus` (see `useGuestSessionState`),
 * so a page needing both identity and attendance pays for a single round trip.
 *
 * Callers must treat an empty result as "not known", never as "no name": this resolves
 * empty for every guest who reached the registry without identifying themselves.
 */
export const useGuestSessionIdentity = ({ eventHandle, skip }: UseGuestSessionIdentityArgs): UseGuestSessionIdentityReturn => {
  const { person, hasDeliveredPerson, loading, isSkipped } = useGuestSessionState({ eventHandle, skip });

  return {
    fullName: person?.fullName || '',
    email: person?.email || '',
    // Keyed on the person half alone, not the whole payload. The cache often holds this
    // guest's identity without their attendance, and identity has no reason to wait on a
    // field it does not read. `cache-and-network` also keeps `loading` true while it
    // revalidates, so a delivered value counts as resolved rather than blocking on the
    // network leg.
    isResolved: isSkipped || hasDeliveredPerson || !loading,
  };
};
